import {
  Modal,
  PrimaryButton,
  SearchField,
  SecondaryButton,
  SnackbarStatus,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useItems } from 'src/hooks/useItems/useItems';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useItemsSelection } from 'src/hooks/useItemsSelection';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { addItemsToMenu } from 'src/api/addItemsToMenu';
import { QueryKeys } from 'src/hooks/QueryKeys';
import chefPlating from 'src/assets/images/chef-plating.png';
import styles from './AddProductsToMenuModal.module.scss';
import { ItemType } from 'src/constants';
import { Range } from '@weareopenr/types';
import SelectableListWithControls from 'src/components/SelectableListWithControls';
import { toMap, getRangeSubtitle } from 'src/helpers';
import { boldMenuName } from 'src/helpers/truncateMenuName/boldMenuName';

interface AddProductsToMenuModalProps {
  open: boolean;
  closeModal: () => void;
  menuName: string;
  menuId: string;
  sectionId: string | null;
  onCreateNewProduct: () => void;
  menuRanges: Range[];
}

export const AddProductsToMenuModal = ({
  open,
  closeModal,
  menuName,
  menuId,
  sectionId,
  onCreateNewProduct,
  menuRanges = [],
}: AddProductsToMenuModalProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const menuRangeIds = useMemo(() => menuRanges.map((range) => range.range_id), [menuRanges]);

  const debouncedSearchTerm = useDebounce(searchTerm);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useItems(
    debouncedSearchTerm,
    50,
    [ItemType.BUNDLE, ItemType.STANDARD, ItemType.NON_FOOD],
    undefined,
    menuRangeIds.length ? menuRangeIds : undefined,
  );

  const productOptions = useMemo(
    () =>
      data?.pages?.flatMap((page) =>
        page.map((item) => ({
          id: item.itemId,
          label: item.name,
          subtitle: getRangeSubtitle(item.ranges),
        })),
      ) ?? [],
    [data],
  );

  const productOptionsMap = useMemo(() => toMap(productOptions, 'id'), [productOptions]);

  const {
    selectedIds: selectedProducts,
    selectedItems: selectedProductOptions,
    handleSelectionChange,
    reset: resetSelection,
  } = useItemsSelection(productOptionsMap);

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);

      const requestBody = selectedProducts.map((itemId) => ({
        item_id: itemId,
        section_id: sectionId ?? null,
      }));

      await addItemsToMenu(menuId, requestBody);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
      const totalAdded = selectedProducts.length;

      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: (
          <>
            {`Successfully added ${totalAdded} product${totalAdded > 1 ? 's' : ''} to `}
            {boldMenuName(menuName)}
          </>
        ),
      });

      return resetFields();
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Failed to add products to menu',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetFields = () => {
    resetSelection();
    setSearchTerm('');
    closeModal();
  };

  return (
    <Modal open={open} closeModal={resetFields} image={chefPlating} imageAlt="chef plating">
      <div className={styles['modal-content']}>
        <Typography size="xxlarge">
          Add Product(s) to <span className={styles['bold-text']}>{menuName}</span>
        </Typography>

        <SelectableListWithControls
          options={productOptions}
          selectedOptionsIds={selectedProducts}
          onOptionChange={handleSelectionChange}
          collapsible={{ selectedOptionsData: selectedProductOptions }}
          listAction={{
            label: 'Create New Product',
            onClick: () => {
              resetFields();
              onCreateNewProduct();
            },
          }}
          listDetails={{ title: 'Products:', isLoading }}
          pagination={{ hasNextPage, onLoadMore: fetchNextPage, isLoadingMore: isFetchingNextPage }}
        >
          <SearchField
            className={styles['search-field']}
            placeholder="Search products..."
            onChange={(e) => setSearchTerm(e.target.value)}
            isDisabled={isSubmitting}
          />
        </SelectableListWithControls>

        <ButtonContainer
          left={
            <SecondaryButton size="full-width" buttonStyle="outline" onClick={resetFields}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              onClick={handleConfirm}
              disabled={selectedProducts.length === 0 || isSubmitting}
            >
              Save
            </PrimaryButton>
          }
        />
      </div>
    </Modal>
  );
};
